/**
 * LA PERSONA, LEIDA DEL CONTRATO spec-persona-v1 (SUJ-T2, ASSIGN #17005 orden 4).
 *
 * Josse aprobo el 21-sep: la persona es la duena del progreso, los Nexos, la asistencia y el
 * contexto; tiene hasta dos clases de llave (panel y operacion) y una o varias membresias. A
 * fijo la forma en el nodo spec-persona-v1 y dijo "pinta contra esta forma desde ya"
 * (COORD #17022). Aqui se lee esa forma, tolerando camelCase y snake_case, y se decide QUE
 * decir cuando no llega.
 *
 * LO QUE NUNCA SE MUESTRA: el PIN ni su hash. Solo "tiene PIN: si/no" (limite del ASSIGN).
 *
 * Sin DOM: lo prueba scripts/check-ficha-persona.mjs.
 */

const texto = (v) => (v === null || v === undefined ? '' : String(v).trim());
const entero = (v) => (Number.isFinite(Number(v)) ? Math.trunc(Number(v)) : 0);
const lista = (v) => (Array.isArray(v) ? v : []);

/** La persona normalizada, o null si la respuesta no trae una. */
export function leerPersona(res) {
  if (!res || !res.ok) return null;
  const cuerpo = res.data || {};
  const p = cuerpo.data && typeof cuerpo.data === 'object' && !Array.isArray(cuerpo.data) ? cuerpo.data : cuerpo;
  const personaId = texto(p.personaId ?? p.persona_id);
  if (!personaId) return null;
  const llaves = p.llaves || {};
  const panel = llaves.panel || {};
  const nexos = p.nexos || {};
  const asis = p.asistencia || {};
  const capa = p.capacitacion || {};
  const con = p.conexiones || {};
  const numero = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  return {
    personaId,
    nombre: texto(p.nombre) || 'sin nombre',
    estado: texto(p.estado) || 'active',
    llavePanel: { tiene: panel.tiene === true, origen: texto(panel.origen) },
    llavesOperacion: lista(llaves.operacion).map((o) => ({
      businessId: texto(o.businessId ?? o.business_id),
      colaboradorId: texto(o.colaboradorId ?? o.colaborador_id),
      tienePin: o.tienePin === true || o.tiene_pin === true
    })),
    membresias: lista(p.membresias).map((m) => ({
      businessId: texto(m.businessId ?? m.business_id),
      negocio: texto(m.negocio) || texto(m.businessId ?? m.business_id),
      rol: texto(m.rol),
      cargo: texto(m.cargo),
      origen: texto(m.origen)
    })),
    nexos: { saldo: entero(nexos.saldo), movimientos: entero(nexos.movimientos), fuente: texto(nexos.fuente) },
    asistencia: { diasConMarca30d: entero(asis.dias_con_marca_30d ?? asis.diasConMarca30d), ultimaMarca: texto(asis.ultima_marca ?? asis.ultimaMarca) },
    capacitacion: { respuestas: entero(capa.respuestas), correctas: entero(capa.correctas), ultima: texto(capa.ultima) },
    // SUJ-T9 (nexo-api 303): null = no hay dato, y la fuente dice por que.
    conexiones: {
      panel: texto(con.ultimaConexionPanel ?? con.ultima_conexion_panel),
      arcade: texto(con.ultimaConexionArcade ?? con.ultima_conexion_arcade),
      fuentePanel: texto(con.fuentePanel ?? con.fuente_panel),
      fuenteArcade: texto(con.fuenteArcade ?? con.fuente_arcade)
    },
    // SUJ-T7 (D-38/D-39): uno por negocio de sus fichas, en el mes operativo. Null en la lista master.
    danos: lista(p.danos).map((d) => ({
      businessId: texto(d.businessId ?? d.business_id),
      desde: texto(d.desde),
      hasta: texto(d.hasta),
      pesos: numero(d.pesos),
      danos: entero(d.danos),
      conteos: entero(d.conteos)
    }))
  };
}

/**
 * Por que no hay persona, dicho segun la causa. Un 404 sin cuerpo es la RUTA que no existe
 * (la API aun no publica); un 404 con problema es la API diciendo que esa ficha no tiene
 * persona. No son lo mismo y no se dicen igual.
 */
export function porQueNoHayPersona(res) {
  if (!res) return { motivo: 'RED', frase: 'No pudimos hablar con la API.' };
  if (res.ok) return leerPersona(res) ? null : { motivo: 'VACIA', frase: 'La API respondió sin persona.' };
  const code = res.code;
  if (code === 404 && !res.details) {
    return { motivo: 'NO_PUBLICADO', frase: 'La ficha de persona se enciende sola cuando la API la publique (SUJ-T1).' };
  }
  // SUJ-T8: la API dice POR QUE (por-usuario: "no es una persona: agente o cuenta de negocio";
  // por-colaborador: "todavia no esta unida"). Se usa su frase; la fija queda de respaldo.
  if (code === 404) return { motivo: 'SIN_PERSONA', frase: texto(res.details?.mensaje) || 'Esta ficha todavía no está unida a una persona.' };
  if (code === 403) return { motivo: 'PERMISO', frase: 'Tu usuario no tiene permiso para ver esta persona.' };
  if (code === 401 || code === 'UNAUTHORIZED') return { motivo: 'SIN_SESION', frase: 'La sesión se cerró: vuelve a entrar.' };
  return { motivo: 'RED', frase: texto(res.error) || 'No pudimos hablar con la API.' };
}

/** Las lineas cortas que la ficha pinta, en idioma de dueno. */
export function lineasDeLaPersona(p) {
  if (!p) return [];
  const negocios = p.membresias.length
    ? p.membresias.map((m) => [m.negocio, m.cargo || m.rol].filter(Boolean).join(' · ')).join(' / ')
    : 'sin negocios';
  const pinEn = p.llavesOperacion.filter((o) => o.tienePin).length;
  const operacion = p.llavesOperacion.length
    ? `${p.llavesOperacion.length} ficha${p.llavesOperacion.length === 1 ? '' : 's'} de operación, ${pinEn} con PIN`
    : 'sin ficha de operación';
  return [
    ['Negocios', negocios],
    ['Entra al panel', p.llavePanel.tiene ? 'sí' : 'no'],
    ['Operación', operacion],
    ['Nexos', `${p.nexos.saldo} (${p.nexos.movimientos} movimientos)`]
  ];
}
